import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import {
  FiDollarSign, FiTrendingUp, FiTrendingDown,
  FiCreditCard, FiLoader, FiArrowUpRight, FiArrowDownRight
} from 'react-icons/fi';
import { format } from 'date-fns';
import { Sparklines, SparklinesLine, SparklinesSpots } from 'react-sparklines';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import useCurrency from '../../hooks/useCurrency';
import { useAuth } from '../../context/AuthContext';
import { useDashboard } from '../../context/DashboardContext';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import {
  fetchLedgerRowsForBalances,
  computeCustomerBalancesFrom,
  computeVendorBalancesFrom,
} from '../../utils/liveBalanceHelper';
import { getCached } from '../../utils/fyDataCache';
import { DASHBOARD_TTL_MS } from '../../utils/dashboardHelper';
import { getScoreboard } from '../../utils/scoreboardData';

const kpiRoutes = {
  revenue: '/reports/sale-summary',
  grossProfit: '/reports/profit-loss',
  cashBalance: '/reports/cash-bank-balances',
  bankBalance: '/reports/cash-bank-balances',
  receivables: '/reports/customer-balances',
  payables: '/reports/vendor-balances',
};

const KPICards = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const { dateRange, refreshing } = useDashboard();
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lean LIVE fallback — computes the 4 balances directly when the precomputed
  // scoreboard doc isn't available yet (before backfill / brand-new company).
  // The old Revenue/Gross-Profit path is gone (those cards were removed), so
  // this no longer scans SI/PS/PI/SR/PR at all — just the ledger + accounts.
  const fetchBalancesLive = useCallback(async (companyId, fyId, branchId) => {
    // GUARD (see B1 in fetchKPIData): without a financial year this whole path
    // is UNBOUNDED — it would download every subTransaction of every year. The
    // caller already skips it; this is the second line of defence.
    if (!fyId || fyId === 'nofy') return;
    const matchesBranch = (t) => !branchId || t.branchId === branchId;
    const { arBalances, apBalances, rawSubTrans, cbAccounts } = await getCached(
      `dashRaw|${companyId}|kpibal|${fyId}|${branchId || 'all'}`,
      async () => {
        // ONE ledger download + ONE accounts download feed all four figures.
        // (Before: customer balances, vendor balances and the raw ledger each
        // fetched the SAME full-year subTransactions — three identical scans —
        // and the customer/vendor account lists were two extra queries on top
        // of the accounts read we already do here.)
        const [rawSubTrans, acctSnap] = await Promise.all([
          fetchLedgerRowsForBalances(companyId, fyId),
          getDocs(collection(db, 'companies', companyId, 'accounts')),
        ]);
        const accounts = acctSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const cbAccounts = accounts
          .map(a => ({ id: a.id, code: a.code || '', level: a.level, isPostable: a.isPostable }))
          .filter(a => (a.level === 5 || a.isPostable) && (a.code.startsWith('11101') || a.code.startsWith('11102')));
        return {
          arBalances: computeCustomerBalancesFrom(accounts, rawSubTrans, branchId),
          apBalances: computeVendorBalancesFrom(accounts, rawSubTrans, branchId),
          rawSubTrans,
          cbAccounts,
        };
      },
      DASHBOARD_TTL_MS
    );

    const totalAR = Object.values(arBalances).reduce((s, v) => s + (Number(v) || 0), 0);
    const totalAP = Object.values(apBalances).reduce((s, v) => s + (Number(v) || 0), 0);
    const includeSub = (t) => t.status === 'Posted' || (t.status === 'Draft' && t.tranTypeCode === 'OB');
    const allSubTrans = rawSubTrans.filter(t => includeSub(t) && matchesBranch(t));
    const cashIds = new Set((cbAccounts || []).filter(a => a.code.startsWith('11101')).map(a => a.id));
    const bankIds = new Set((cbAccounts || []).filter(a => a.code.startsWith('11102')).map(a => a.id));
    const haveAccts = (cbAccounts || []).length > 0;
    const isCash = (st) => haveAccts ? cashIds.has(st.accountId) : st.accountCode?.startsWith('11101');
    const isBank = (st) => haveAccts ? bankIds.has(st.accountId) : st.accountCode?.startsWith('11102');
    const cashEntries = allSubTrans.filter(isCash);
    const bankEntries = allSubTrans.filter(isBank);
    const cashBalance = cashEntries.reduce((s, st) => s + (st.debitAmount || 0) - (st.creditAmount || 0), 0);
    const bankBalance = bankEntries.reduce((s, st) => s + (st.debitAmount || 0) - (st.creditAmount || 0), 0);
    const arEntriesAll = allSubTrans.filter(st => st.accountCode?.startsWith('11201'));
    const apEntriesAll = allSubTrans.filter(st => st.accountCode?.startsWith('21101'));
    const buildTrend = (entries, getDelta, buckets = 30) => {
      const sorted = entries.filter(e => e.docDate).sort((a, b) => String(a.docDate).localeCompare(String(b.docDate)));
      if (sorted.length === 0) return Array(buckets).fill(0);
      const endDate = dateRange.endDate || new Date();
      const startDate = dateRange.startDate || new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
      const totalMs = Math.max(1, endDate.getTime() - startDate.getTime());
      const out = [];
      for (let i = 1; i <= buckets; i++) {
        const cutoff = format(new Date(startDate.getTime() + (totalMs * i / buckets)), 'yyyy-MM-dd');
        let running = 0;
        for (const e of sorted) { if (e.docDate > cutoff) break; running += getDelta(e); }
        out.push(running);
      }
      return out;
    };
    setKpiData({
      cashBalance, bankBalance, totalAR, totalAP,
      cashTrend: buildTrend(cashEntries, e => (e.debitAmount || 0) - (e.creditAmount || 0)),
      bankTrend: buildTrend(bankEntries, e => (e.debitAmount || 0) - (e.creditAmount || 0)),
      arTrend: buildTrend(arEntriesAll, e => (e.debitAmount || 0) - (e.creditAmount || 0)),
      apTrend: buildTrend(apEntriesAll, e => (e.creditAmount || 0) - (e.debitAmount || 0)),
    });
  }, [dateRange]);

  const fetchKPIData = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const companyId = activeCompany.id;
      const fyId = activeFinancialYear?.id || null;
      const branchId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;

      // NO ACTIVE FINANCIAL YEAR → show zeros and stop here.
      // Why: every fallback below is scoped BY financial year. Without one, the
      // scoreboard has no node to read and the live fallback degrades into an
      // unbounded scan of the ENTIRE subTransactions collection (all years) —
      // measured at ~750K reads for a single dashboard open. A company with no
      // active year has no current-year balances to show anyway, so zeros are
      // the honest figure and cost nothing.
      if (!fyId) {
        setKpiData({
          cashBalance: 0, bankBalance: 0, totalAR: 0, totalAP: 0,
          cashTrend: [0], bankTrend: [0], arTrend: [0], apTrend: [0],
        });
        return;
      }

      // Refresh button → simply RE-READ the scoreboard doc (~1 read). The doc is
      // kept exact by the maintainBalanceScoreboard trigger on every ledger write
      // (baseline set by the one-time backfill), so re-reading gives fresh
      // numbers. We deliberately DO NOT force a full-ledger recompute here — that
      // scans the entire subTransactions collection (100K+ reads on big
      // companies) on EVERY click. An exact recompute stays available through
      // recomputeBalanceScoreboard (with a cooldown) and the nightly reconcile.

      // PRIMARY: the precomputed scoreboard doc — ~1 read, survives reloads and
      // every POS terminal, cheap regardless of company size. Maintained by the
      // maintainBalanceScoreboard Cloud Function on every ledger write.
      // Shared with MoneyPositionWidget and ReceivablePayableWidget via
      // scoreboardData.getScoreboard — all three panels come out of ONE read
      // instead of fetching the same document three times.
      const readScoreboard = async (forceRefresh = false) => {
        const data = await getScoreboard(companyId, forceRefresh);
        if (!data) return false;
        // Trust the scoreboard ONLY after a full baseline recompute (backfill).
        // Before that the doc holds only post-deploy deltas — use live instead.
        const fyNode = data?.recomputedAt ? data?.fy?.[fyId] : null;
        if (!fyNode) return false;
        // A branch with no node in the scoreboard is NOT "all zeros" — it means
        // the scoreboard can't answer for this branch. Returning {} here used to
        // render four zero cards, i.e. a wrong number shown as fact. Treat it as
        // unusable so the recompute / live path decides instead.
        const bucket = branchId ? fyNode.branch?.[branchId] : (fyNode.all || {});
        if (!bucket) return false;
        const monthly = data?.monthly?.[fyId] || {};
        const months = Object.keys(monthly).sort();
        const trendFor = (fam) => {
          let run = 0;
          const pts = months.map(m => { run += Number(monthly[m]?.[fam] || 0); return Math.round(run); });
          return pts.length ? pts : [0];
        };
        setKpiData({
          cashBalance: Number(bucket.cash || 0),
          bankBalance: Number(bucket.bank || 0),
          totalAR: Number(bucket.receivable || 0),
          totalAP: Number(bucket.payable || 0),
          cashTrend: trendFor('cash'),
          bankTrend: trendFor('bank'),
          arTrend: trendFor('receivable'),
          apTrend: trendFor('payable'),
        });
        return true;
      };

      let used = false;
      try { used = await readScoreboard(); } catch (e) { /* fall through */ }

      // Scoreboard has no node for this FY (new financial year / brand-new
      // company). Ask the server to recompute ONCE — one scan that fixes the
      // doc for every device — instead of each device scanning the full-year
      // ledger 3× per 2h forever (the old fallback cost ~150K reads per
      // dashboard open on big companies).
      if (!used) {
        // Guard bookkeeping: the DONE key is written only AFTER the recompute
        // actually succeeds. Writing it up-front (the old behaviour) meant one
        // failed recompute permanently disabled the cheap path for that tab —
        // every later dashboard open went straight to the full-ledger scan.
        // A failure instead records a timestamp so it is retried after a short
        // cooldown. At most ONE recompute attempt per mount either way.
        const doneKey = `amal_kpi_recompute_ok|${companyId}|${fyId}`;
        const failKey = `amal_kpi_recompute_fail|${companyId}|${fyId}`;
        const FAIL_COOLDOWN_MS = 10 * 60 * 1000;
        try {
          const lastFail = Number(sessionStorage.getItem(failKey) || 0);
          const coolingDown = lastFail > 0 && (Date.now() - lastFail) < FAIL_COOLDOWN_MS;
          if (!sessionStorage.getItem(doneKey) && !coolingDown) {
            try {
              const recompute = httpsCallable(functions, 'recomputeBalanceScoreboard');
              await recompute({ companyId });
              sessionStorage.setItem(doneKey, '1');   // only on success
              sessionStorage.removeItem(failKey);
              // Force a fresh read: the server just rewrote the document, so the
              // copy cached moments ago is the stale pre-recompute version.
              used = await readScoreboard(true);
              if (!used) {
                // Recompute succeeded but still no node → this FY (or this
                // branch) genuinely has no ledger rows yet. Zeros are correct.
                setKpiData({
                  cashBalance: 0, bankBalance: 0, totalAR: 0, totalAP: 0,
                  cashTrend: [0], bankTrend: [0], arTrend: [0], apTrend: [0],
                });
                used = true;
              }
            } catch (e) {
              // Recompute unavailable/failed — remember WHEN so the next mount
              // inside the cooldown skips it, but a later one retries.
              sessionStorage.setItem(failKey, String(Date.now()));
            }
          }
        } catch (e) { /* sessionStorage blocked — fall back to live below */ }
      }

      if (!used) await fetchBalancesLive(companyId, fyId, branchId);
    } catch (error) {
      console.error('Error fetching KPI data:', error);
      setKpiData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeFinancialYear, activeBranch, fetchBalancesLive]);

  useEffect(() => {
    fetchKPIData();
  }, [fetchKPIData]);

  // Dashboard Refresh button → re-read the (trigger-maintained) scoreboard doc.
  useEffect(() => {
    if (refreshing) fetchKPIData();
  }, [refreshing, fetchKPIData]);

  if (loading) {
    return (
      <LoadingWrap>
        <FiLoader className="spin" size={20} />
        <span>Loading KPI data...</span>
      </LoadingWrap>
    );
  }

  if (!kpiData) return null;

  const cards = [
    {
      id: 'cashBalance',
      title: 'Cash',
      value: kpiData.cashBalance,
      icon: FiDollarSign,
      color: kpiData.cashBalance >= 0 ? '#0EA5E9' : '#EF4444',
      accent: kpiData.cashBalance >= 0 ? '#E0F2FE' : '#FEE2E2',
      trend: kpiData.cashTrend,
    },
    {
      id: 'bankBalance',
      title: 'Bank',
      value: kpiData.bankBalance,
      icon: FiCreditCard,
      color: kpiData.bankBalance >= 0 ? '#10B981' : '#EF4444',
      accent: kpiData.bankBalance >= 0 ? '#D1FAE5' : '#FEE2E2',
      trend: kpiData.bankTrend,
    },
    {
      id: 'receivables',
      title: 'Receivable',
      value: kpiData.totalAR,
      icon: FiArrowDownRight,
      color: '#F59E0B',
      accent: '#FEF3C7',
      trend: kpiData.arTrend,
    },
    {
      id: 'payables',
      title: 'Payable',
      value: kpiData.totalAP,
      icon: FiArrowUpRight,
      color: '#8B5CF6',
      accent: '#EDE9FE',
      trend: kpiData.apTrend,
    },
  ];

  return (
    <Container>
      {cards.map((card) => (
        <KPICard
          key={card.id}
          as={motion.div}
          whileHover={{ y: -3, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate(kpiRoutes[card.id])}
          $color={card.color}
          $accent={card.accent}
        >
          <CardTop>
            <IconCircle $color={card.color} $accent={card.accent}>
              <card.icon size={18} />
            </IconCircle>
            {card.badge && (
              <KPIBadge $color={card.color}>{card.badge}</KPIBadge>
            )}
          </CardTop>
          <KPIValue>
            <CountUp
              end={card.value}
              duration={1.2}
              separator=","
              formattingFn={(val) => formatCurrency(val)}
            />
          </KPIValue>
          <KPITitle>{card.title}</KPITitle>
          {card.trend && card.trend.length > 1 && (
            <SparkWrap>
              <Sparklines data={card.trend} width={200} height={40} margin={4}>
                <SparklinesLine
                  color={card.color}
                  style={{ strokeWidth: 2, fill: card.color, fillOpacity: 0.15 }}
                />
                <SparklinesSpots size={2} style={{ fill: card.color, stroke: card.color }} />
              </Sparklines>
            </SparkWrap>
          )}
          <CardAccentBar $color={card.color} />
        </KPICard>
      ))}
    </Container>
  );
};

// Styled Components
const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  padding: ${spacing.xl};
  color: var(--color-text-secondary);
  font-size: ${typography.fontSize.sm};

  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${spacing.sm};
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SparkWrap = styled.div`
  margin-top: ${spacing.xs};
  width: 100%;
  svg { width: 100%; height: 32px; display: block; }
`;

const KPICard = styled.div`
  position: relative;
  background: var(--color-background-default);
  border-radius: ${borderRadius.lg};
  padding: ${spacing.sm} ${spacing.md};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--color-border-light);
  overflow: hidden;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.sm};
`;

const IconCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$accent};
  color: ${props => props.$color};
`;

const KPIBadge = styled.span`
  font-size: 11px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.$color};
  background: ${props => props.$color + '15'};
  padding: 2px 8px;
  border-radius: 10px;
`;

const KPIValue = styled.div`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: var(--color-text-primary);
  line-height: 1.2;
  margin-bottom: 2px;
  white-space: nowrap;
`;

const KPITitle = styled.div`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardAccentBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${props => props.$color};
  opacity: 0.6;
`;

export default KPICards;
